import { gql } from '@apollo/client';
import { client as apolloClient } from '@xbcb/apollo-client';
import {
  AccountType,
  DateSearchOperator,
  KeywordSearchOperator,
  RecordType,
} from '@xbcb/shared-types';
import { WorkOrderStatus } from '@xbcb/work-order-types';
import { getIsIorActivated } from '@xbcb/party-utils';
import { get } from 'lodash';
import { getOneQueryLite, SearchQuery } from '@xbcb/shared-queries';
import { validateAddress } from 'libs/validateAddress';
import { DocumentTag } from '@xbcb/document-types';
import { UsBusinessStructure } from '@xbcb/party-types';
import { showValidationErrors, setModal } from '@xbcb/ui-utils';
import { executeMutation } from 'libs/executeMutation';
import { ModalKey } from '@xbcb/ui-types';
import type { UsIor } from '@xbcb/api-gateway-client';
import { AppRecordSubmitButton } from 'routes/types';

const GET_US_IOR_VERSION = getOneQueryLite({
  recordName: RecordType.US_IOR,
  fields: 'version iorNumber { value }',
  queryName: 'getUsIorVersion',
});

const searchQueryFields = `
  id
  version
  operator {
    id
  }
  tags {
    key
    value
  }
  activations {
    status
    customsBroker {
      id
    }
  }
  activationStatus
`;

const searchQuery = SearchQuery({
  recordName: RecordType.US_IOR,
  fields: searchQueryFields,
});

const updateUsIorActivationStatus = gql`
  mutation updateUsIorActivationStatus(
    $id: ID!
    $version: Int!
    $usCustomsBrokerId: ID!
    $activationStatus: ActivationStatus!
  ) {
    updateUsIorActivationStatus(
      id: $id
      version: $version
      usCustomsBrokerId: $usCustomsBrokerId
      activationStatus: $activationStatus
    ) {
      usIor {
        id
        version
      }
    }
  }
`;

const validatePoa = (usIor: UsIor) => {
  const additionalErrors = [];
  let hasDirect = false;
  let hasMaster = false;
  let hasSub = false;
  let hasCC = false;
  const { documents = [], businessStructure, addresses } = usIor || {};
  documents.forEach(({ documentTags = [], deleted, tags = [] }) => {
    const rejected = tags.find((tag) => tag.key === 'rejected');
    if (documentTags.length && !deleted && !rejected) {
      for (const documentTag of documentTags) {
        switch (documentTag) {
          case DocumentTag.DIRECT_POWER_OF_ATTORNEY: {
            hasDirect = true;
            break;
          }
          case DocumentTag.MASTER_POWER_OF_ATTORNEY: {
            hasMaster = true;
            break;
          }
          case DocumentTag.SUB_POWER_OF_ATTORNEY: {
            hasSub = true;
            break;
          }
          case DocumentTag.CORPORATE_CERTIFICATION: {
            hasCC = true;
            break;
          }
          default: {
            break;
          }
        }
      }
    }
  });

  if (
    addresses?.mailing?.countryCode !== 'US' &&
    businessStructure &&
    ![
      UsBusinessStructure.INDIVIDUAL,
      UsBusinessStructure.SOLE_PROPRIETOR,
    ].includes(businessStructure as UsBusinessStructure) &&
    !hasCC
  ) {
    additionalErrors.push({
      title: 'Missing CC',
      messages: [
        `Importer is foreign and a ${businessStructure} so needs a Corporate Certification.`,
      ],
    });
  }
  if (!hasDirect && (!hasMaster || !hasSub)) {
    additionalErrors.push({
      title: 'Missing Poa',
      messages: [`Importer needs a Power of Attorney.`],
    });
  }
  return additionalErrors;
};

const validateActivationFields = (usIor: UsIor) => {
  const { addresses, industry, businessStructure } = usIor;

  const additionalErrors = [
    ...validateAddress({
      addressType: 'mailing',
      addressObject: addresses?.mailing,
    }),
    ...validateAddress({
      addressType: 'physical',
      addressObject: addresses?.physical,
    }),
  ];

  if (!industry) {
    additionalErrors.push({
      title: 'Missing Vertical',
      messages: ['Importer must have a vertical'],
    });
  }

  if (!businessStructure) {
    additionalErrors.push({
      title: 'Missing Business Structure',
      messages: ['Importer must have a business structure'],
    });
  }

  return additionalErrors;
};

export const createUsIorActivateButton = (
  pathToUsIor: string[],
  brokerPath = 'broker.usCustomsBroker.id',
): AppRecordSubmitButton => ({
  key: 'activateUsIor',
  text: 'Activate',
  skipUpdateRecord: () => true,
  skipValidation: () => true,
  show: ({ existingRecord, currentUser }) => {
    const isOperator = currentUser.accountType === AccountType.OPERATOR;
    const invalidWorkOrderStatus = [
      WorkOrderStatus.COMPLETED,
      WorkOrderStatus.BLOCKED,
      WorkOrderStatus.CANCELED,
    ].includes(existingRecord?.status);
    const usIor = get(existingRecord, pathToUsIor);
    const customsBrokerId = get(existingRecord, brokerPath.split('.'));
    const isUsIorActivated = getIsIorActivated({
      ior: usIor,
      customsBrokerId,
    });
    return (
      isOperator &&
      !isUsIorActivated &&
      !existingRecord.deleted?.time &&
      !invalidWorkOrderStatus &&
      get(existingRecord, [...pathToUsIor, 'iorNumber', 'value']) &&
      !get(existingRecord, [...pathToUsIor, 'cbp5106RejectionReasons'])
    );
  },
  onSubmit: async ({ updatedRecord, dispatch }) => {
    const usIor = get(updatedRecord, pathToUsIor);
    const additionalErrors = [
      ...validateActivationFields(usIor),
      ...validatePoa(usIor),
    ];

    if (additionalErrors?.length) {
      showValidationErrors(additionalErrors);
      return;
    } else {
      const { data } = await apolloClient.query({
        query: GET_US_IOR_VERSION,
        variables: { id: usIor.id },
        errorPolicy: 'all',
      });
      const { version, iorNumber } = data?.usIor as UsIor;
      const iorNumberValue = iorNumber?.value;
      if (iorNumberValue) {
        const { data: searchData } = await apolloClient.query({
          query: searchQuery,
          variables: {
            input: {
              searchCriteria: {
                iorNumber: {
                  values: [iorNumberValue],
                  operator: KeywordSearchOperator.EQUALS,
                },
                deletedTime: {
                  operator: DateSearchOperator.DOES_NOT_EXIST,
                },
              },
            },
          },
          errorPolicy: 'all',
        });
        const { results } = searchData || {};
        const existingUsIor = results?.[0] as UsIor | undefined;
        if (existingUsIor) {
          const { id, name } = existingUsIor;
          dispatch(
            setModal({
              key: ModalKey.MARK_WORK_ORDER_READY,
              props: {
                visible: true,
                existingImporterId: id,
                existingImporterName: name,
              },
            }),
          );
          return;
        }
      }

      const customsBrokerId = get(updatedRecord, brokerPath.split('.'));
      await executeMutation({
        mutation: updateUsIorActivationStatus,
        successMessage: 'IOR Activation status updated',
        variables: {
          id: usIor?.id,
          version: version || usIor.version,
          usCustomsBrokerId: customsBrokerId,
          activationStatus: 'ACTIVATED',
        },
      });
    }
  },
});
